import React, {
  useMemo,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'react-window';

import {
  fetchMoreMetaBooths,
  selectBoothPageInfo,
  selectTotalFilteredBoothsCount,
} from 'models/cms/booths';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import GenericBoothPreview from './GenericBoothPreview';
import HeaderComponent from './HeaderComponent';
import FooterWrapper from './FooterWrapper';

const ExhibitorRowContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  border-radius: 0 !important;
`;

const BoothPreviewRow = styled.div`
  display: flex;
  border-radius: 0 !important;
  flex-direction: row;
`;

export const CMSFetchMoreFooter = () => {
  const dispatch = useDispatch();
  const pageInfo = useSelector(selectBoothPageInfo);

  useEffect(() => {
    if (!pageInfo.hasNextPage) return;
    dispatch(fetchMoreMetaBooths());
  }, [dispatch, pageInfo]);

  return null;
};

export const CMSFetchMoreIndicator = ({ offsetTop }: any) => {
  const pageInfo = useSelector(selectBoothPageInfo);
  if (!pageInfo.fetching) return null;
  return (
    <FooterWrapper offsetTop={offsetTop}>
      <CircularLoadingIndicator />
    </FooterWrapper>
  );
};

interface RowProps {
  index: number;
  data: string[];
  itemsPerRow: number;
  rowCount: number;
  style: Record<string, any>;
  isFiltered: boolean;
  containerHeight: number;
  headerOffset: number;
  hasFilter: boolean;
}

const ListRow = ({
  index,
  data,
  itemsPerRow,
  style,
  rowCount,
  headerOffset,
  containerHeight,
  hasFilter,
  isFiltered,
}: RowProps) => {
  const totalFilteredBoothsCount = useSelector(selectTotalFilteredBoothsCount);
  const firstIndex = useMemo(() => (index * itemsPerRow), [index, itemsPerRow]);
  const exhibitorRow = useMemo(() => {
    const lastIndex = firstIndex + itemsPerRow;
    const rowData = [];

    for (let i = firstIndex; i < lastIndex; i += 1) {
      if (i >= data.length) break;
      rowData.push(data[i]);
    }
    return rowData;
  }, [data, itemsPerRow, firstIndex]);

  const difference = useMemo(() => {
    const footerTop = rowCount * style.height + headerOffset;
    return Math.max(0, containerHeight - footerTop - 90);
  }, [containerHeight, headerOffset, rowCount, style.height]);

  return (
    <ExhibitorRowContainer style={{
      ...style,
      top: `${parseFloat(style.top) + headerOffset}px`,
    }}
    >
      {(index === 0) && (
        <HeaderComponent
          hasFilter={hasFilter}
          isFiltered={isFiltered}
          filterLength={totalFilteredBoothsCount}
          headerOffset={headerOffset}
        />
      )}
      <BoothPreviewRow>
        {exhibitorRow.map((boothId, exhibitorIndex) => (
          <GenericBoothPreview
            key={boothId}
            index={exhibitorIndex}
            boothId={boothId}
          />
        ))}
      </BoothPreviewRow>
      {(index >= rowCount - 2) && (
        <CMSFetchMoreFooter />
      )}
      {(index === rowCount - 1) && (
        <CMSFetchMoreIndicator offsetTop={difference} />
      )}
    </ExhibitorRowContainer>
  );
};

export default React.memo(ListRow, areEqual);
